import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { AriaComponent, GridComponent, LegendComponent, TitleComponent, TooltipComponent } from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';
import { ECBasicOption } from 'echarts/types/dist/shared';
import { ECHARTS_LINE_TYPES, ECHARTS_SYMBOLS } from '@shared/constants/charts.constants';
import {
  ECHARTS_BASE_OPTIONS,
  ECHARTS_GENERATIVE_THEME,
  ECHARTS_GENERATIVE_THEME_NAME,
} from '@shared/constants/custom-report-theme.constants';
import { IChatV2ComponentChunk } from '@shared/interfaces/ai-executor.interface';
import { severityColors } from '../../../../constants/charts.constants';
import { EchartsService } from '../../../../services/echarts.service';
import { DEFAULT_LEGEND_WIDTH_PX, estimateLegendRows, LEGEND_ITEM_GAP_PX, LEGEND_ROW_HEIGHT_PX } from '../chart-legend.utility';
import {
  AXIS_LABEL_FONT_FAMILY,
  AXIS_LABEL_FONT_SIZE_PX,
  AXIS_LABEL_INTERVAL_ALL,
  AXIS_LABEL_MARGIN_PX,
  estimateNumericAxisTickLabelWidthPx,
} from '../chart-axis.utility';
import { getAxisLinePixelY, getRenderedContentBBox } from '../chart-measurement.utility';
import { ChartLayout } from '../chart-layout.constants';
import { toMarkdownTable } from '../generative-components.utils';

export interface ILineChartSeries {
  name: string;
  data: number[];
}

type TimeSeriesDataPoint = [string, number];

interface IAccessibleSeriesRow {
  seriesName: string;
  points: Array<{ category: string; value: number }>;
}

// Time-series ticks are auto-formatted and unrotated (one line), which containLabel already
// sizes correctly on its own — no rotation, so the rotated-label measurement pass doesn't apply.
const TIME_AXIS_TICK_LINE_HEIGHT_PX = 16;
const TIME_AXIS_NAME_GAP_PX = TIME_AXIS_TICK_LINE_HEIGHT_PX + AXIS_LABEL_MARGIN_PX + ChartLayout.X_AXIS_NAME_GAP_BREATHING_PX;
const TIME_AXIS_GRID_HEIGHT_PX = ChartLayout.GRID_PLOT_HEIGHT_PX + TIME_AXIS_TICK_LINE_HEIGHT_PX + AXIS_LABEL_MARGIN_PX;

@Component({
  selector: 'app-ai-chat-line-chart',
  templateUrl: './ai-chat-line-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [EchartsService],
  imports: [CommonModule],
  standalone: true,
})
export class AiChatLineChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() public chunk: IChatV2ComponentChunk;

  @ViewChild('chartTarget') private chartTarget: ElementRef<HTMLDivElement>;

  public title: string = '';
  public accessibleSeriesData: IAccessibleSeriesRow[] = [];
  public chartHeight: number = 300;

  private chart: echarts.EChartsType;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private echartsService: EchartsService,
  ) {}

  public ngAfterViewInit(): void {
    this.initChart();
  }

  public ngOnChanges(): void {
    if (this.chart) {
      this.initChart();
    }
  }

  public ngOnDestroy(): void {
    this.echartsService.disposeAll();
  }

  public static serialize(data: Record<string, unknown>): string {
    const title = (data['title'] as string) ?? '';
    const xAxisLabel = (data['xAxisLabel'] as string) ?? '';
    const yAxisLabel = (data['yAxisLabel'] as string) ?? '';
    const categories = (data['categories'] as string[]) ?? [];
    const series = (data['series'] as ILineChartSeries[]) ?? [];

    const lines: string[] = [];
    if (title) {
      lines.push(title);
    }
    if (xAxisLabel || yAxisLabel) {
      lines.push(`${xAxisLabel} / ${yAxisLabel}`);
    }

    const columns = [xAxisLabel || 'Category', ...series.map((s: ILineChartSeries) => s.name)];
    const rows = categories.map((category: string, i: number) => [
      category,
      ...series.map((s: ILineChartSeries) => String(s.data[i] ?? 0)),
    ]);
    lines.push(toMarkdownTable(columns, rows));

    return lines.join('\n');
  }

  private buildSeriesOption(series: ILineChartSeries[], categories: string[], isTimeSeries: boolean): ECBasicOption['series'] {
    return series.map((s: ILineChartSeries, i: number) => {
      const severityColor = (severityColors as Record<string, string | undefined>)[s.name.toLowerCase()];
      const seriesData: number[] | TimeSeriesDataPoint[] = isTimeSeries
        ? categories.map((cat: string, idx: number): TimeSeriesDataPoint => [cat, s.data[idx] ?? 0])
        : s.data;
      return {
        name: s.name,
        type: 'line' as const,
        data: seriesData,
        smooth: true,
        symbol: ECHARTS_SYMBOLS[i % ECHARTS_SYMBOLS.length],
        lineStyle: { type: ECHARTS_LINE_TYPES[i % ECHARTS_LINE_TYPES.length] },
        ...(severityColor ? { itemStyle: { color: severityColor } } : {}),
      };
    });
  }

  private initChart(): void {
    if (!this.chartTarget) {
      return;
    }

    const data = this.chunk?.data ?? {};
    const categories = (data['categories'] as string[]) ?? [];
    const series = (data['series'] as ILineChartSeries[]) ?? [];
    this.title = (data['title'] as string) ?? '';
    const xAxisLabel = (data['xAxisLabel'] as string) ?? '';
    const yAxisLabel = (data['yAxisLabel'] as string) ?? '';
    const isTimeSeries = (data['isTimeSeries'] as boolean) ?? false;

    this.accessibleSeriesData = series.map((s: ILineChartSeries) => ({
      seriesName: s.name,
      points: categories.map((cat: string, i: number) => ({ category: cat, value: s.data[i] ?? 0 })),
    }));

    // Single series: legend would just repeat what's already on the chart.
    const showLegend = series.length > 1;

    // grid.left uses the same tick-width estimate as nameGap so estimate error cancels out instead of pushing the name off-screen.
    const allValues = series.flatMap((s: ILineChartSeries) => s.data);
    const estimatedTickWidthPx = estimateNumericAxisTickLabelWidthPx(allValues);
    const yAxisNameGapPx = estimatedTickWidthPx + AXIS_LABEL_MARGIN_PX + ChartLayout.Y_AXIS_NAME_GAP_BREATHING_PX;
    const gridLeftPx = estimatedTickWidthPx + ChartLayout.Y_AXIS_NAME_LINE_HEIGHT_PX + ChartLayout.Y_AXIS_NAME_LEFT_BREATHING_PX;

    echarts.use([LineChart, AriaComponent, GridComponent, LegendComponent, TitleComponent, TooltipComponent, SVGRenderer]);
    echarts.registerTheme(ECHARTS_GENERATIVE_THEME_NAME, ECHARTS_GENERATIVE_THEME);

    const seriesOption = this.buildSeriesOption(series, categories, isTimeSeries);
    const yAxisOption = {
      type: 'value' as const,
      name: yAxisLabel,
      nameLocation: 'middle' as const,
      nameGap: yAxisNameGapPx,
      nameRotate: ChartLayout.Y_AXIS_NAME_ROTATE_DEGREES,
    };

    if (isTimeSeries) {
      this.renderTimeSeriesChart(gridLeftPx, yAxisOption, seriesOption, series, xAxisLabel, showLegend);
      return;
    }
    this.renderCategoryChart(gridLeftPx, yAxisOption, seriesOption, categories, xAxisLabel, showLegend);
  }

  // Time-series ticks are unrotated, so containLabel sizes them correctly on its own —
  // render once with a fixed reservation instead of the rotated-label measurement pass.
  private renderTimeSeriesChart(
    gridLeftPx: number,
    yAxisOption: ECBasicOption['yAxis'],
    seriesOption: ECBasicOption['series'],
    series: ILineChartSeries[],
    xAxisLabel: string,
    showLegend: boolean,
  ): void {
    const containerWidthPx = this.chartTarget.nativeElement.clientWidth || DEFAULT_LEGEND_WIDTH_PX;
    const legendRows = showLegend
      ? estimateLegendRows(
          series.map((s: ILineChartSeries) => s.name),
          containerWidthPx,
        )
      : 0;
    const nameBottomPx =
      ChartLayout.GRID_TOP_PX + ChartLayout.GRID_PLOT_HEIGHT_PX + TIME_AXIS_NAME_GAP_PX + ChartLayout.X_AXIS_NAME_HEIGHT_PX;
    const legendTopPx = nameBottomPx + ChartLayout.LEGEND_TOP_GAP_PX;
    this.chartHeight = showLegend
      ? legendTopPx + legendRows * LEGEND_ROW_HEIGHT_PX + ChartLayout.LEGEND_BOTTOM_PADDING_PX
      : nameBottomPx + ChartLayout.LEGEND_BOTTOM_PADDING_PX;
    this.changeDetectorRef.detectChanges();

    this.chart = this.echartsService.initialize(this.chartTarget.nativeElement, ECHARTS_GENERATIVE_THEME_NAME);
    this.chart.setOption({
      ...ECHARTS_BASE_OPTIONS,
      legend: showLegend
        ? { show: true, type: 'plain', orient: 'horizontal', top: legendTopPx, itemGap: LEGEND_ITEM_GAP_PX }
        : { show: false },
      grid: { top: ChartLayout.GRID_TOP_PX, height: TIME_AXIS_GRID_HEIGHT_PX, left: gridLeftPx, right: '4%', containLabel: true },
      xAxis: { type: 'time', name: xAxisLabel, nameLocation: 'middle', nameGap: TIME_AXIS_NAME_GAP_PX, boundaryGap: false },
      yAxis: yAxisOption,
      series: seriesOption,
    } as ECBasicOption);
  }

  private renderCategoryChart(
    gridLeftPx: number,
    yAxisOption: ECBasicOption['yAxis'],
    seriesOption: ECBasicOption['series'],
    categories: string[],
    xAxisLabel: string,
    showLegend: boolean,
  ): void {
    this.chartHeight = ChartLayout.PASS_1_SCRATCH_HEIGHT_PX;
    this.changeDetectorRef.detectChanges();

    this.chart = this.echartsService.initialize(this.chartTarget.nativeElement, ECHARTS_GENERATIVE_THEME_NAME);

    const xAxisLabelOption = {
      interval: AXIS_LABEL_INTERVAL_ALL,
      rotate: ChartLayout.X_AXIS_LABEL_ROTATE_DEGREES,
      fontSize: AXIS_LABEL_FONT_SIZE_PX,
      fontFamily: AXIS_LABEL_FONT_FAMILY,
    };

    // A partial setOption merge defers the re-render, so measuring right after reads stale values — always pass the full option.
    const buildOption = (
      gridHeightPx: number,
      xAxisName: string | undefined,
      xAxisNameGapPx: number,
      legendOption: Record<string, unknown>,
    ): ECBasicOption => ({
      ...ECHARTS_BASE_OPTIONS,
      legend: legendOption,
      grid: { top: ChartLayout.GRID_TOP_PX, height: gridHeightPx, left: gridLeftPx, right: '4%', containLabel: true },
      xAxis: {
        type: 'category',
        data: categories,
        name: xAxisName,
        nameLocation: 'middle',
        nameGap: xAxisNameGapPx,
        boundaryGap: false,
        axisLabel: xAxisLabelOption,
      },
      yAxis: yAxisOption,
      series: seriesOption,
    });

    // Pass 1: render without the x-axis name/legend to measure the labels' real extent.
    this.chart.setOption(buildOption(ChartLayout.PASS_1_GRID_HEIGHT_PLACEHOLDER_PX, undefined, 0, { show: false }), true);

    const axisLineY = getAxisLinePixelY(this.chart);
    const rotatedLabelsBBox = getRenderedContentBBox(this.chart);
    const realLabelHeightPx = rotatedLabelsBBox.y + rotatedLabelsBBox.height - axisLineY;
    const gridHeightPx = ChartLayout.GRID_PLOT_HEIGHT_PX + realLabelHeightPx + AXIS_LABEL_MARGIN_PX;
    const xAxisNameGapPx = realLabelHeightPx + AXIS_LABEL_MARGIN_PX + ChartLayout.X_AXIS_NAME_GAP_BREATHING_PX;
    const nameBottomPx =
      ChartLayout.GRID_TOP_PX + ChartLayout.GRID_PLOT_HEIGHT_PX + xAxisNameGapPx + ChartLayout.X_AXIS_NAME_HEIGHT_PX;
    const legendTopPx = nameBottomPx + ChartLayout.LEGEND_TOP_GAP_PX;
    const legendOption = showLegend
      ? { show: true, type: 'plain', orient: 'horizontal', top: legendTopPx, itemGap: LEGEND_ITEM_GAP_PX }
      : { show: false };

    // Pass 2: size the grid to the real label height, then add the x-axis name and legend.
    this.chart.setOption(buildOption(gridHeightPx, xAxisLabel, xAxisNameGapPx, legendOption), true);

    const finalBBox = getRenderedContentBBox(this.chart);
    this.chartHeight = finalBBox.y + finalBBox.height + ChartLayout.LEGEND_BOTTOM_PADDING_PX;
    this.changeDetectorRef.detectChanges();
    this.chart.resize();
  }
}
